import { useState, useRef, useEffect, useCallback } from 'react';
import { Wand2, Loader2, Check, X, Pencil } from 'lucide-react';

function AutoTextarea({ value, onChange, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      {...props}
    />
  );
}

export function FixButton({ text, onApply, disabled, voice }) {
  const [state, setState] = useState('idle');
  const [suggested, setSuggested] = useState('');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL || '';
  const tooShort = !text || text.trim().length < 3;

  const handleFix = async () => {
    if (tooShort) return;
    setState('loading');
    setError('');
    try {
      const res = await fetch(`${API}/api/ai/fix-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: text.trim(), voice: voice || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al corregir');
      }
      const data = await res.json();
      setSuggested(data.suggested);
      setEditValue(data.suggested);
      setState('preview');
    } catch (e) {
      setError(e.message);
      setState('error');
    }
  };

  const handleApply = () => {
    onApply(editing ? editValue : suggested);
    close();
  };

  const close = useCallback(() => {
    setState('idle');
    setSuggested('');
    setEditing(false);
    setError('');
    setEditValue('');
  }, []);

  // Close on Escape
  useEffect(() => {
    if (state !== 'preview') return;
    const handler = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state, close]);

  // Button states: idle, error, loading
  if (state !== 'preview') {
    return (
      <div className="fix-btn-wrapper">
        <button
          className={`fix-btn ${state === 'loading' ? 'fix-btn-loading' : ''}`}
          onClick={handleFix}
          disabled={disabled || tooShort || state === 'loading'}
          title={tooShort ? 'Texto muy corto' : state === 'error' ? error : 'Corregir con IA'}
          data-testid={state === 'loading' ? 'fix-text-loading' : 'fix-text-btn'}
        >
          {state === 'loading' ? (
            <><Loader2 className="h-3 w-3 animate-spin" /><span>Corrigiendo...</span></>
          ) : state === 'error' ? (
            <><span className="fix-btn-error">!</span><span>FIX</span></>
          ) : (
            <><Wand2 className="h-3 w-3" /><span>FIX</span></>
          )}
        </button>
      </div>
    );
  }

  // Modal overlay for preview
  return (
    <>
      <div className="fix-btn-wrapper">
        <button className="fix-btn" disabled><Wand2 className="h-3 w-3" /><span>FIX</span></button>
      </div>
      <div className="fix-overlay" onClick={close} data-testid="fix-overlay">
        <div className="fix-modal" onClick={(e) => e.stopPropagation()} data-testid="fix-preview">
          <div className="fix-modal-header">
            <Wand2 className="h-4 w-4" />
            <span>Sugerencia IA</span>
            <button className="fix-modal-close" onClick={close} data-testid="fix-cancel-btn">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="fix-modal-body">
            <div className="fix-original">
              <span className="fix-section-label">Original:</span>
              <p className="fix-section-text">{text}</p>
            </div>

            <div className="fix-suggested">
              <span className="fix-section-label">Corrección:</span>
              {editing ? (
                <AutoTextarea
                  className="fix-modal-textarea"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                  data-testid="fix-edit-textarea"
                />
              ) : (
                <p className="fix-section-text fix-section-suggested" data-testid="fix-suggested-text">{suggested}</p>
              )}
            </div>
          </div>

          <div className="fix-modal-footer">
            <button className="fix-modal-btn fix-modal-apply" onClick={handleApply} data-testid="fix-apply-btn">
              <Check className="h-3.5 w-3.5" /> Reemplazar
            </button>
            {!editing ? (
              <button className="fix-modal-btn fix-modal-edit" onClick={() => setEditing(true)} data-testid="fix-edit-btn">
                <Pencil className="h-3.5 w-3.5" /> Editar
              </button>
            ) : null}
            <button className="fix-modal-btn fix-modal-cancel" onClick={close}>
              <X className="h-3.5 w-3.5" /> Cancelar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
